import * as React from 'react';
import { cn } from '../utils/cn';
import { ChevronRight } from 'lucide-react';

// ─── Breadcrumb.Item ────────────────────────────────────────────────────

export interface BreadcrumbItemProps {
  /** Click handler for navigation */
  onClick?: () => void;
  /** Additional class names */
  className?: string;
  /** Inline style */
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

export function BreadcrumbItem({ onClick, className, style, children }: BreadcrumbItemProps) {
  const isClickable = !!onClick;

  return (
    <span
      className={cn(
        'inline-flex items-center text-sm font-body',
        isClickable
          ? 'text-muted-foreground hover:text-accent cursor-pointer transition-colors duration-fast'
          : 'text-foreground',
        className
      )}
      style={style}
      onClick={onClick}
      role={isClickable ? 'link' : undefined}
    >
      {children}
    </span>
  );
}

// ─── Breadcrumb ─────────────────────────────────────────────────────────

export interface BreadcrumbProps {
  /** Separator between items (default: chevron) */
  separator?: React.ReactNode;
  /** Additional class names */
  className?: string;
  /** Inline style */
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

function BreadcrumbBase({
  separator,
  className,
  style,
  children,
}: BreadcrumbProps) {
  const items = React.Children.toArray(children).filter(Boolean);

  return (
    <nav
      className={cn('flex items-center gap-1.5 font-body', className)}
      style={style}
      aria-label="Breadcrumb"
    >
      {items.map((child, index) => (
        <React.Fragment key={index}>
          {index > 0 && (
            <span className="text-muted-foreground/50" aria-hidden="true">
              {separator || <ChevronRight size={14} />}
            </span>
          )}
          {child}
        </React.Fragment>
      ))}
    </nav>
  );
}

// Compound component for antd compat: Breadcrumb.Item
export const Breadcrumb = Object.assign(BreadcrumbBase, { Item: BreadcrumbItem });
